import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    getConsent, setConsent, isPublicMarketingPath, loadOptionalScripts, revokeOptionalScripts,
} from "@/lib/consent";

const btnBase = "h-11 px-6 rounded-md font-display font-semibold text-sm inline-flex items-center justify-center " +
    "bg-foreground text-background hover:opacity-90 transition-opacity " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const linkBase = "underline underline-offset-4 text-foreground hover:opacity-80 transition-opacity rounded-sm " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const Banner = ({ onManage }) => (
    <div
        role="region"
        aria-label="Cookie consent"
        data-testid="cookie-banner"
        className="fixed bottom-0 inset-x-0 z-[70] border-t border-border bg-card shadow-[0_-8px_30px_rgba(0,0,0,0.12)]"
    >
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-4 sm:py-5 flex flex-col lg:flex-row lg:items-center gap-4">
            <p className="text-sm leading-relaxed text-foreground flex-1 min-w-0">
                AuditVanguard uses essential cookies to keep your workspace secure. On public pages only,
                we use optional analytics cookies to understand how visitors find us — never inside your
                workspace, never for ad tracking.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 shrink-0">
                <button type="button" className={btnBase} onClick={() => setConsent("accepted")}
                    aria-label="Accept optional cookies" data-testid="cookie-accept-btn">
                    Accept
                </button>
                <button type="button" className={btnBase} onClick={() => setConsent("rejected")}
                    aria-label="Reject optional cookies" data-testid="cookie-reject-btn">
                    Reject
                </button>
                <button type="button" className={`${linkBase} text-sm`} onClick={onManage} data-testid="cookie-manage-link">
                    Manage preferences
                </button>
                <Link to="/privacy#cookies" className={`${linkBase} text-sm`} data-testid="cookie-privacy-link">
                    Privacy Policy
                </Link>
            </div>
        </div>
    </div>
);

const PreferencesDialog = ({ open, onOpenChange }) => {
    const [ads, setAds] = useState(getConsent() === "accepted");
    useEffect(() => { if (open) setAds(getConsent() === "accepted"); }, [open]);
    const save = () => {
        setConsent(ads ? "accepted" : "rejected");
        toast.success("Cookie preferences saved");
        onOpenChange(false);
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="cookie-preferences-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Cookie className="h-4 w-4" strokeWidth={1.5} /> Cookie preferences
                    </DialogTitle>
                    <DialogDescription>
                        Choose which cookies we may use on our public pages. Your logged-in workspace never
                        uses optional cookies.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4">
                        <div>
                            <div className="font-display font-semibold text-sm">Necessary</div>
                            <p className="text-sm text-muted-foreground mt-1">Login and security. Always on.</p>
                        </div>
                        <Switch checked disabled aria-label="Necessary cookies, always on" data-testid="cookie-toggle-necessary" />
                    </div>
                    <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-4">
                        <div>
                            <div className="font-display font-semibold text-sm">Advertising and analytics</div>
                            <p className="text-sm text-muted-foreground mt-1">
                                Helps us measure Instagram ads and understand site usage.
                            </p>
                        </div>
                        <Switch checked={ads} onCheckedChange={setAds}
                            aria-label="Allow advertising and analytics cookies" data-testid="cookie-toggle-ads" />
                    </div>
                </div>
                <DialogFooter>
                    <button type="button" className={btnBase} onClick={save} data-testid="cookie-save-btn">
                        Save choices
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Mounted once in App: shows the banner on public marketing pages, enforces
// script gating on every route change, and appends a slim utility strip with
// "Cookie settings" + "Privacy Policy" to the bottom of every public page.
export const ConsentGate = () => {
    const { pathname } = useLocation();
    const marketing = isPublicMarketingPath(pathname);
    const [choice, setChoice] = useState(getConsent());
    const [panelOpen, setPanelOpen] = useState(false);

    useEffect(() => {
        const onChanged = (e) => setChoice(e.detail);
        const onOpen = () => setPanelOpen(true);
        window.addEventListener("cookie-consent-changed", onChanged);
        window.addEventListener("open-cookie-settings", onOpen);
        return () => {
            window.removeEventListener("cookie-consent-changed", onChanged);
            window.removeEventListener("open-cookie-settings", onOpen);
        };
    }, []);

    useEffect(() => {
        if (marketing) loadOptionalScripts();
        else revokeOptionalScripts();
    }, [pathname, marketing]);

    if (!marketing) return null;
    return (
        <>
            <div className="border-t border-border/60 bg-background">
                <div className="max-w-7xl mx-auto px-5 sm:px-10 py-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-muted-foreground">
                    <button type="button" onClick={() => setPanelOpen(true)}
                        className="hover:text-foreground transition-colors underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                        data-testid="footer-cookie-settings">
                        Cookie settings
                    </button>
                    <Link to="/privacy#cookies"
                        className="hover:text-foreground transition-colors underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                        data-testid="footer-privacy-policy">
                        Privacy Policy
                    </Link>
                </div>
            </div>
            {!choice && <Banner onManage={() => setPanelOpen(true)} />}
            <PreferencesDialog open={panelOpen} onOpenChange={setPanelOpen} />
        </>
    );
};
